import React from 'react'
import { cn } from '../../utils/cn'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  /**
   * Input label
   */
  label?: string

  /**
   * Error message
   */
  error?: string

  /**
   * Helper text
   */
  helperText?: string

  /**
   * Input type - if 'textarea', renders textarea element
   */
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search' | 'textarea'

  /**
   * Full width input
   */
  fullWidth?: boolean

  /**
   * Icon to display before input
   */
  iconBefore?: React.ReactNode

  /**
   * Icon to display after input
   */
  iconAfter?: React.ReactNode

  /**
   * Textarea rows (only for type="textarea")
   */
  rows?: number
}

export const Input = React.forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(
  (
    {
      className,
      label,
      error,
      helperText,
      type = 'text',
      fullWidth = false,
      iconBefore,
      iconAfter,
      disabled,
      id,
      rows = 3,
      ...props
    },
    ref
  ) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`
    const hasError = !!error

    const inputClasses = cn(
      // Base styles
      'w-full px-3 py-2 text-sm rounded-lg',
      'border transition-colors',
      'placeholder:text-deep-charcoal/40',
      'focus:outline-none focus:ring-2 focus:ring-toast-gold focus:ring-offset-0',

      // Normal state
      !hasError && 'border-soft-tan bg-white text-deep-charcoal',

      // Error state
      hasError && 'border-coral-pop bg-coral-pop/5 text-deep-charcoal',

      // Disabled state
      disabled && 'bg-deep-charcoal/5 cursor-not-allowed opacity-60',

      // Icon padding adjustments
      iconBefore && 'pl-10',
      iconAfter && 'pr-10',

      className
    )

    const InputElement = type === 'textarea' ? 'textarea' : 'input'

    return (
      <div className={cn('flex flex-col gap-1', fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              'text-sm font-medium',
              hasError ? 'text-coral-pop' : 'text-deep-charcoal'
            )}
          >
            {label}
          </label>
        )}

        <div className="relative">
          {iconBefore && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-deep-charcoal/50">
              {iconBefore}
            </div>
          )}

          <InputElement
            ref={ref as any}
            id={inputId}
            type={type === 'textarea' ? undefined : type}
            className={inputClasses}
            disabled={disabled}
            rows={type === 'textarea' ? rows : undefined}
            aria-invalid={hasError}
            aria-describedby={
              error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
            }
            {...(props as any)}
          />

          {iconAfter && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-deep-charcoal/50">
              {iconAfter}
            </div>
          )}
        </div>

        {error && (
          <p id={`${inputId}-error`} className="text-xs text-coral-pop">
            {error}
          </p>
        )}

        {!error && helperText && (
          <p id={`${inputId}-helper`} className="text-xs text-deep-charcoal/60">
            {helperText}
          </p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export default Input
